<template>
  <section class="color-new-light-grey">
    <v-container :class="{ 'pa-0': $vuetify.breakpoint.smAndDown }">

      <v-row justify="center">
        <v-col class="shrink">
          <h1 class="text-h1">{{ $t('messenger.title') }}</h1>
        </v-col>
      </v-row>

      <v-row no-gutters>
        <v-col cols="12">
          <v-card :class="{ 'radius-card': $vuetify.breakpoint.mdAndUp }">
            <v-card-text>

              <!-- Strong auth required notice -->
              <template v-if="!userIsStronglyAuthenticated">
                <v-row justify="center" class="py-6">
                  <v-col cols="12" class="text-center">
                    <v-icon color="color-new-link-blue" :size="$vuetify.breakpoint.smAndDown ? 56 : 72" class="mb-4">fas fa-user-shield</v-icon>
                  </v-col>
                  <v-col cols="12" sm="8" md="6">
                    <p class="text-h6 font-weight-regular mt-6">{{ $t('messenger.strongAuthRequired') }}</p>
                    <p class="text-h6 font-weight-regular">{{ $t('messenger.strongAuthRequiredHint') }}</p>
                  </v-col>
                </v-row>
              </template>

              <!-- Main messenger content (strong auth only) -->
              <template v-else>

                <!-- Status row: spinner while loading, then one of the two mutually-exclusive buttons -->
                <v-row justify="center" class="mt-4 mb-2">
                  <v-col cols="12" sm="8" md="6" class="d-flex align-center justify-center flex-column flex-md-row">
                    <template v-if="!historyLoaded">
                      <v-progress-circular indeterminate color="primary" :size="24" :width="2" class="mr-3" />
                      <span class="text-body-2 color-new-body-grey--text">{{ $t('messenger.loadingHistory') }}</span>
                    </template>

                    <!-- Active conversation exists -->
                    <template v-else-if="hasActiveConversation">
                      <span class="text-body-2 font-weight-medium mb-2 mb-md-0 mr-md-4">{{ $t('messenger.hasActiveConversation') }}</span>
                      <v-btn tile color="primary" :loading="starting" @click="openActiveConversation">
                        <v-icon left small>fas fa-comments</v-icon>
                        {{ $t('messenger.openConversation') }}
                      </v-btn>
                    </template>

                    <!-- No active conversation -->
                    <template v-else>
                      <v-btn tile color="primary" :loading="starting" @click="startNewConversation">
                        <v-icon left small>fas fa-envelope</v-icon>
                        {{ $t('messenger.startNewConversation') }}
                      </v-btn>
                    </template>
                  </v-col>
                </v-row>

                <v-row justify="center">
                  <!-- Closed conversations table -->
                  <v-col v-if="closedConversations.length > 0" cols="12" sm="10" md="8">
                    <h4 class="text-subtitle-1 mb-2 mt-2 text-center">{{ $t('messenger.previousConversations') }}</h4>
                    <v-data-table
                      :items="pagedConversations"
                      :headers="closedConversationHeaders"
                      hide-default-header
                      hide-default-footer
                      :items-per-page="-1"
                      :mobile-breakpoint="0"
                      class="conversation-table"
                      @click:row="item => openHistory(item.idx)"
                    >
                      <template #item.icon>
                        <v-icon small color="color-grey">fas fa-chevron-right</v-icon>
                      </template>
                      <template #item.topicNr="{ item }">
                        <span class="color-new-body-grey--text">{{ getTopicLabel(item.topicNr) }}</span>
                      </template>
                      <template #item.endedAt="{ item }">
                        <span class="color-new-body-grey--text text-no-wrap">{{ item.endedAt | formatDate }}</span>
                      </template>
                      <template #item.action="{ item }">
                        <span
                          class="text-caption font-weight-bold color-new-link-blue--text text-no-wrap"
                          style="letter-spacing: 0.05em;"
                          @click.stop="openHistory(item.idx)"
                        >AVAA VIESTI</span>
                      </template>
                    </v-data-table>
                    <v-row justify="center" align="center" no-gutters class="mt-1">
                      <v-btn icon small :disabled="conversationPage === 1" @click="conversationPage--">
                        <v-icon small>fas fa-chevron-left</v-icon>
                      </v-btn>
                      <span
                        v-for="p in conversationPageCount"
                        :key="p"
                        class="mx-1 text-body-2"
                        :class="p === conversationPage ? 'color-new-link-blue--text font-weight-bold' : 'color-new-body-grey--text'"
                        style="cursor: pointer; min-width: 24px; text-align: center;"
                        @click="conversationPage = p"
                      >{{ p }}</span>
                      <v-btn icon small :disabled="conversationPage === conversationPageCount" @click="conversationPage++">
                        <v-icon small>fas fa-chevron-right</v-icon>
                      </v-btn>
                    </v-row>
                  </v-col>
                </v-row>

                <!-- Active conversation modal -->
                <conversation-modal v-model="conversationModal"></conversation-modal>

                <!-- New conversation dialog -->
                <new-conversation-dialog
                  v-model="newConversationDialog"
                  @start="onConversationStart"
                ></new-conversation-dialog>

                <!-- Closed conversation history dialog -->
                <v-dialog
                  v-model="historyDialog"
                  :fullscreen="dialogSettings.fullscreen"
                  :scrollable="dialogSettings.scrollable"
                  :hide-overlay="dialogSettings.hideoverlay"
                  :transition="dialogSettings.transition"
                  :width="dialogSettings.width"
                  :max-width="dialogSettings.maxwidth"
                >
                  <conversation-history-dialog
                    v-if="selectedConversation !== null"
                    :conversation="closedConversations[selectedConversation]"
                    @close="historyDialog = false"
                  ></conversation-history-dialog>
                </v-dialog>

              </template>

            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </section>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { apiResource } from '@/util/resources'
  import diag from '@/util/messengerDiagnostics'
  import GenesysService from '@/services/genesysService'
  import ConversationModal from '@/components/Chat/ConversationModal.vue'
  import NewConversationDialog from '@/components/Chat/NewConversationDialog.vue'
  import ConversationHistoryDialog from '@/components/Chat/ConversationHistoryDialog.vue'

  export default {
    name: 'MessengerView',

    components: {
      'conversation-modal': ConversationModal,
      'new-conversation-dialog': NewConversationDialog,
      'conversation-history-dialog': ConversationHistoryDialog
    },

    data: function () {
      return {
        conversationModal: false,
        newConversationDialog: false,
        historyDialog: false,
        selectedConversation: null,
        conversationPage: 1,
        starting: false,
        topics: [],
        topicsLoaded: false
      }
    },

    computed: {
      ...mapGetters({
        debtor: 'debtor',
        debts: 'allDebts',
        locale: 'locale',
        userIsStronglyAuthenticated: 'signicatAuthenticated',
        hasActiveConversation: 'hasActiveConversation'
      }),
      ...mapGetters('messenger', ['closedConversations', 'historyLoaded']),
      closedConversationsTableItems: function () {
        return this.closedConversations.map(function (conv, idx) {
          return { idx: idx, endedAt: conv.endedAt, topicNr: conv.topicNr }
        })
      },
      conversationPageCount: function () {
        return Math.ceil(this.closedConversations.length / 5) || 1
      },
      pagedConversations: function () {
        var start = (this.conversationPage - 1) * 5
        return this.closedConversationsTableItems.slice(start, start + 5)
      },
      closedConversationHeaders: function () {
        var headers = [
          { text: '', value: 'icon', width: '36px', sortable: false },
          { text: '', value: 'topicNr', sortable: false },
          { text: '', value: 'endedAt', width: '100px', sortable: false }
        ]
        if (this.$vuetify.breakpoint.mdAndUp) {
          headers.push({ text: '', value: 'action', width: '110px', sortable: false })
        }
        return headers
      },
      dialogSettings: function () {
        return {
          fullscreen: this.$vuetify.breakpoint.smAndDown,
          scrollable: true,
          hideoverlay: this.$vuetify.breakpoint.smAndDown,
          transition: this.$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition',
          width: this.$vuetify.breakpoint.smAndDown ? 'auto' : '860px',
          maxwidth: this.$vuetify.breakpoint.smAndDown ? 'none' : '90%'
        }
      }
    },

    created: function () {
      if (!this.userIsStronglyAuthenticated) return

      console.log('[MessengerView] created — hasActiveConversation:', this.hasActiveConversation)

      // Always load closed conversation history for the sidebar
      this.$store.dispatch('messenger/fetchHistory')
      this.fetchTopics()

      // Only init Genesys when the DB confirms an active conversation to restore
      if (this.hasActiveConversation) {
        console.log('[MessengerView] DB flag hasActiveConversation=true — initialising Genesys to restore session')
        this.initGenesys()
      } else {
        console.log('[MessengerView] DB flag hasActiveConversation=false — showing start button, Genesys not initialised')
      }
    },

    methods: {
      // ── Genesys SDK init ──────────────────────────────────────────────────

      initGenesys: function () {
        console.log('[MessengerView] initGenesys called')
        window.__genesysGetToken = this.fetchGenesysAuthData

        if (window.$genesys && !this.$store.state.messenger.ready) {
          console.log('[MessengerView] stale $genesys instance found (not ready) — clearing it')
          window.$genesys = null
        }
        if (window.$genesys) {
          console.log('[MessengerView] $genesys already initialised and ready — skipping re-init')
          return
        }

        console.log('[MessengerView] creating new GenesysService and calling init()')
        window.$genesys = new GenesysService(this.$store)
        window.$genesys.init()

        if (typeof window.__genesysActivateAuth === 'function') {
          console.log('[MessengerView] calling __genesysActivateAuth()')
          window.__genesysActivateAuth()
        }
      },

      fetchGenesysAuthData: function () {
        console.log('[MessengerView] fetchGenesysAuthData — requesting token from API')
        return apiResource.get('api/authentication/genesys/token').then(function (r) {
          console.log('[MessengerView] Genesys token fetched successfully')
          return r.data
        }).catch(function (err) {
          console.error('[MessengerView] Failed to fetch Genesys auth token', err)
          return null
        })
      },

      // ── Waits for messenger/ready via a watcher-based Promise ─────────────
      // Only resolves after MessagingService.ready fires (plugin fully loaded).

      waitForReady: function () {
        var self = this
        if (self.$store.state.messenger.ready) {
          console.log('[MessengerView] waitForReady — already ready, resolving immediately')
          return Promise.resolve()
        }
        console.log('[MessengerView] waitForReady — watching for messenger/ready...')
        return new Promise(function (resolve) {
          var unwatch = self.$watch(
            function () { return self.$store.state.messenger.ready },
            function (val) {
              if (val) {
                console.log('[MessengerView] waitForReady — ready flag became true, resolving')
                unwatch()
                resolve()
              }
            }
          )
        })
      },

      // ── Open active conversation (Path A) ────────────────────────────────

      openActiveConversation: async function () {
        console.log('[MessengerView] openActiveConversation — waiting for SDK ready')
        this.starting = true
        try {
          await this.waitForReady()
          console.log('[MessengerView] openActiveConversation — SDK ready, opening modal')
        } finally {
          this.starting = false
        }
        apiResource.post('api/messenger/seen').catch(function (err) {
          console.warn('[chat] Failed to mark messages as seen', err)
        })
        this.$store.commit('clearLastUnseenAnsweredMessage')
        this.conversationModal = true
      },

      // ── Start new conversation (Path B) ──────────────────────────────────

      startNewConversation: function () {
        console.log('[MessengerView] startNewConversation — opening new conversation dialog')
        this.newConversationDialog = true
      },

      onConversationStart: async function (payload) {
        // Start a fresh diagnostics window shared by the view, the SDK service and
        // the store so the posted log captures the full ordered timeline.
        diag.reset()
        var log = function (msg) { diag.log('view', msg) }

        log('start — topicNr:' + payload.topicNr + ' debtNrs:' + payload.debtNrs.join(',') + ' Message: ' + payload.message)
        this.newConversationDialog = false
        this.starting = true

        try {
          log('initGenesys — start')
          this.initGenesys()
          var g = window.$genesys
          log('initGenesys — done')

          // 1. Wait for the MessagingService plugin to finish loading.
          log('whenReady — start')
          await g.whenReady()
          log('whenReady — done (MessagingService.ready)')

          // 2. Wait for the WebSocket session to actually go live. This is the key
          //    ordering gate: it guarantees startConversation/sendMessage below run
          //    against an established transport, not one that is still connecting.
          log('whenSessionStarted — start')
          var session = await g.whenSessionStarted()
          log('whenSessionStarted — done ' + JSON.stringify(session))

          // 3. Attach conversation metadata. This MUST happen before startConversation:
          //    the SDK captures the Database contents at the JOIN event, so writing the
          //    custom attributes now guarantees the new conversation is created with them
          //    (agent-side routing depends on this). "Auto start conversation" is disabled
          //    in the deployment, so no JOIN happens until step 4.
          var selectedDebts = this.debts.filter(function (d) { return payload.debtNrs.indexOf(d.nr) !== -1 })
          var latestSelectedDebt = selectedDebts.slice().sort(function (a, b) { return b.nr - a.nr })[0]
          var customAttrs = {
            DebtorNr:         String(this.debtor.nr),
            AvailableDebtNrs: this.debts.map(function (d) { return d.nr }).join(','),
            GroupNr:          latestSelectedDebt ? String(latestSelectedDebt.groupNr) : '',
            Language:         this.locale,
            TopicNr:          String(payload.topicNr),
            Topic:            payload.topicLabel || '',
            SelectedDebtNrs:  payload.debtNrs.join(',')
          }
          log('customAttrs — ' + JSON.stringify(customAttrs))
          log('setCustomAttributes — start')
          await g.setCustomAttributes(customAttrs)
          log('setCustomAttributes — done')

          // 4. Open the conversation. With autoStart disabled this is what sends the
          //    JOIN event, creating the conversation with the attributes set in step 3.
          //    If a conversation from a previous page session was restored, the service
          //    joins it instead of creating a new one.
          log('startConversation — dispatching')
          await this.$store.dispatch('messenger/startConversation')
          log('startConversation — done')

          // 5. Send the opening message and wait for the server echo to confirm delivery.
          if (payload.message) {
            log('sendMessage — start')
            try {
              var confirmed = await this.$store.dispatch('messenger/sendMessage', payload.message)
              log('sendMessage — done confirmed=' + confirmed)
            } catch (err) {
              log('sendMessage — failed: ' + (err && (err.message || String(err))))
              console.warn('[MessengerView] onConversationStart — opening message send failed', err)
            }
          }

          log('opening conversation modal')
          this.conversationModal = true
          apiResource.post('api/messenger/log', { type: 'ConversationStart', log: diag.dump() }).catch(function (err) {
            console.warn('[MessengerView] Failed to post ConversationStart log', err)
          })
        } catch (e) {
          log('error: ' + (e && (e.message || String(e))))
          console.error('[MessengerView] onConversationStart — error', e)
          apiResource.post('api/messenger/log', { type: 'ConversationStartError', log: diag.dump() }).catch(function (err) {
            console.warn('[MessengerView] Failed to post ConversationStartError log', err)
          })
          this.$store.commit('setMessage', { type: 'error', text: this.$t('messenger.startConversationError') })
        } finally {
          this.starting = false
        }
      },

      // ── Topics ───────────────────────────────────────────────────────────

      fetchTopics: function () {
        var self = this
        apiResource.get('api/messenger/topics', { params: { includeDisabled: true } }).then(function (response) {
          self.topics = (response.data && response.data.responseObject) || response.data || []
          self.topicsLoaded = true
        }).catch(function (err) {
          console.warn('[MessengerView] Failed to fetch topics', err)
          self.topicsLoaded = true
        })
      },

      getTopicLabel: function (topicNr) {
        if (!this.topicsLoaded) return ''
        if (!topicNr) return '-'
        var topic = this.topics.find(function (t) { return t.nr === topicNr })
        if (!topic) return '-'
        var locale = this.locale
        if (locale === 'se' && topic.sv) return topic.sv
        if (locale === 'en' && topic.en) return topic.en
        return topic.fi || topic.en || topic.sv || '-'
      },

      // ── Closed conversation history ───────────────────────────────────────

      openHistory: function (idx) {
        this.selectedConversation = idx
        this.historyDialog = true
      }
    }
  }
</script>

<style scoped>
.conversation-table >>> tbody tr {
  cursor: pointer;
}
.conversation-table >>> td {
  padding-left: 4px !important;
  padding-right: 4px !important;
}
</style>
